import React, { forwardRef } from "react";
import { default as ReactDataTable } from "react-data-table-component";
import PropTypes from "prop-types";
import checkIfPropExistsInPropTypes from "../helpers/checkIfPropExistsInPropTypes";
import { Translate } from "react-localize-redux";

const Checkbox = forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = React.useRef();
  const resolvedRef = ref || defaultRef;

  React.useEffect(() => {
    if (resolvedRef && resolvedRef.current)
      resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return (
    <>
      <input
        type="checkbox"
        className="form-check-input"
        ref={resolvedRef}
        {...rest}
      />
    </>
  );
});

const DataTable = ({ columns, data, ...rest }) => {
  React.useEffect(() => {
    checkIfPropExistsInPropTypes(rest, DataTable);
  }, [rest]);

  return (
    <ReactDataTable
      columns={columns}
      data={data}
      persistTableHead={rest.persistTableHead || true}
      paginationRowsPerPageOptions={
        rest.paginationRowsPerPageOptions || [10, 30, 50, 100]
      }
      noDataComponent={
        rest.noDataComponent || (
          <div className="p-3">
            <Translate id="no_records_to_display" />
          </div>
        )
      }
      customStyles={{
        headCells: {
          style: {
            fontSize: "15px",
            fontWeight: "bold",
            textTransform: "uppercase",
          },
        },
      }}
      selectableRowsHighlight={true}
      selectableRowsComponent={Checkbox}
      {...rest}
    />
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.node,
      ]).isRequired,
      selector: PropTypes.func,
      sortable: PropTypes.bool,
      format: PropTypes.func,
      compact: PropTypes.bool,
      hide: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }).isRequired
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  striped: PropTypes.bool,
  highlightOnHover: PropTypes.bool,
  pointerOnHover: PropTypes.bool,
  persistTableHead: PropTypes.bool,
  onRowClicked: PropTypes.func,
  selectableRows: PropTypes.bool,
  onSelectedRowsChange: PropTypes.func,
  selectableRowDisabled: PropTypes.func,
  selectableRowsNoSelectAll: PropTypes.bool,
  clearSelectedRows: PropTypes.bool,
  pagination: PropTypes.bool,
  paginationPerPage: PropTypes.number,
  paginationTotalRows: PropTypes.number,
  paginationRowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  paginationResetDefaultPage: PropTypes.bool,
  onChangePage: PropTypes.func,
  conditionalRowStyles: PropTypes.arrayOf(
    PropTypes.shape({
      when: PropTypes.func.isRequired,
      style: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
      classNames: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  noDataComponent: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.element,
  ]),
  onChangeRowsPerPage: PropTypes.func,
  paginationServer: PropTypes.bool,
  progressPending: PropTypes.bool,
  progressComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onSort: PropTypes.func,
  sortServer: PropTypes.bool,
};

export default DataTable;
